import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  Tv as TvIcon,
  TvOff as TvOffIcon,
} from "@mui/icons-material";
import io from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const GAME_LABELS = {
  buzzer: "Beat the Buzzer",
  insta_quiz: "Insta Quiz",
  higher_or_lower: "Higher or Lower",
  spinning_wheel: "Spin the Wheel",
  beerillioner: "Who Wants to Beer Millionaire",
};

const getAdminToken = () =>
  localStorage.getItem("admin_token") || localStorage.getItem("auth_token");

/**
 * ON/OFF toggle for a game on the public /spectate screen.
 * Only one game can be live at a time. Turning OFF returns to MYM idle default.
 * Turning ON while another game is live asks for confirmation first.
 * Parent can call ref.ensureOn(sessionId) to auto-start this game, or open the
 * switch modal if a different game is already live.
 */
const SpectatorOnOffButton = forwardRef(({
  gameType,
  gameLabel,
  sessionId = null,
  disabled = false,
  size = "medium",
}, ref) => {
  const [spectatorState, setSpectatorState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const pendingSessionIdRef = useRef(null);

  const label = gameLabel || GAME_LABELS[gameType] || gameType;
  const isOn =
    spectatorState?.status === "live" && spectatorState?.game_type === gameType;
  const otherGameType =
    spectatorState?.status === "live" &&
    spectatorState?.game_type &&
    spectatorState.game_type !== gameType
      ? spectatorState.game_type
      : null;
  const otherLabel = otherGameType
    ? GAME_LABELS[otherGameType] || otherGameType
    : null;

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/spectator/live`);
      if (res.ok) {
        const data = await res.json();
        setSpectatorState(data);
        return data;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, []);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;
    socket.on("connect", () => {
      socket.emit("spectator:join");
    });
    socket.on("spectator:updated", (payload) => {
      setSpectatorState(payload);
    });
    return () => {
      socket.emit("spectator:leave");
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const startSpectator = useCallback(async (overrideSessionId) => {
    const sid =
      overrideSessionId !== undefined
        ? overrideSessionId
        : pendingSessionIdRef.current !== null
          ? pendingSessionIdRef.current
          : sessionId;
    pendingSessionIdRef.current = null;

    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/spectator/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({
          game_type: gameType,
          session_id: sid || null,
          config: {},
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to turn spectator on");
      }
      setSpectatorState(data);
    } catch (e) {
      setError(e.message || "Failed to turn spectator on");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }, [gameType, sessionId]);

  const stopSpectator = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/spectator/stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to turn spectator off");
      }
      setSpectatorState(data);
    } catch (e) {
      setError(e.message || "Failed to turn spectator off");
    } finally {
      setBusy(false);
    }
  };

  const ensureOn = useCallback(async (overrideSessionId) => {
    setError(null);
    const current = await fetchState();
    const liveGame = current?.status === "live" ? current.game_type : null;

    if (!liveGame || liveGame === gameType) {
      await startSpectator(overrideSessionId);
      return;
    }

    pendingSessionIdRef.current =
      overrideSessionId !== undefined ? overrideSessionId : sessionId;
    setConfirmOpen(true);
  }, [fetchState, gameType, sessionId, startSpectator]);

  useImperativeHandle(ref, () => ({ ensureOn }), [ensureOn]);

  const handleClick = () => {
    setError(null);
    if (isOn) {
      stopSpectator();
      return;
    }
    if (otherGameType) {
      pendingSessionIdRef.current = sessionId;
      setConfirmOpen(true);
      return;
    }
    startSpectator();
  };

  return (
    <>
      <Button
        variant={isOn ? "contained" : "outlined"}
        color={isOn ? "success" : "inherit"}
        size={size}
        startIcon={
          busy ? (
            <CircularProgress size={16} color="inherit" />
          ) : isOn ? (
            <TvIcon />
          ) : (
            <TvOffIcon />
          )
        }
        disabled={disabled || busy}
        onClick={handleClick}
        sx={
          isOn
            ? undefined
            : {
                color: "rgba(255,255,255,0.85)",
                borderColor: "rgba(255,255,255,0.35)",
              }
        }
      >
        Spectator: {isOn ? "ON" : "OFF"}
      </Button>

      {error ? (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}>
        <DialogTitle>Switch Spectator View?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{otherLabel}</strong> is currently ON in Spectator View. Only one game
            can be live at a time.
          </Typography>
          <Typography sx={{ mt: 1.5 }}>
            Stop <strong>{otherLabel}</strong> and switch to <strong>{label}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              pendingSessionIdRef.current = null;
              setConfirmOpen(false);
            }}
            disabled={busy}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => startSpectator()}
            disabled={busy}
            autoFocus
          >
            {busy ? "Switching..." : "Yes, switch"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

SpectatorOnOffButton.displayName = "SpectatorOnOffButton";

export default SpectatorOnOffButton;
